import { useContext, useState } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import { Box, Button } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import EditableList from '../EditableList/EditableList';
import GoogleRatingMatchDialog from '../Entertainment/GoogleRatingMatchDialog';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function EntertainmentManagement() {
  const authCtx = useContext(AuthContext);
  const [googleRatingOpen, setGoogleRatingOpen] = useState(false);
  const optionalLocationText = value => {
    if (value === null || value === undefined) return null;
    const trimmed = String(value).trim();
    return trimmed === '' ? null : trimmed;
  };

  // Access rights
  if (!authCtx.checkRight('entertainment:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const entertainmentSchema = {
    entertainmentName: {
      label: 'Name',
      api: { itemField: 'entertainmentName' },
      placeholder: 'Name eingeben',
      type: 'text',
      required: true,
      minWidth: '180px',
    },
    longName: {
      label: 'Vollständiger Name',
      api: { itemField: 'longName' },
      placeholder: '-',
      type: 'text',
      required: false,
      readOnly: true,
      excludeFromAdd: true,
      minWidth: '220px',
    },
    branch: {
      label: 'Filiale',
      type: 'select',
      api: {
        fieldName: 'branch',
        itemField: 'branch',
        idName: '_id',
        resource: 'branch',
        labelField: 'longName',
        valueField: '_id',
        skipSpecial: true,
      },
      placeholder: 'Filiale auswählen',
      required: true,
      minWidth: '160px',
    },
    legalEntity: {
      label: 'Rechtsträger',
      type: 'select',
      api: {
        fieldName: 'legalEntity',
        itemField: 'legalEntity',
        idName: '_id',
        resource: 'legalEntity',
        labelField: 'legalName',
        labelBuilder: item =>
          `${item.legalName || ''}${
            item.street ? ` – ${item.street} ${item.houseNumber || ''}` : ''
          }${
            item.postcode || item.city ? `, ${item.postcode || ''} ${item.city || ''}` : ''
          }`.trim(),
        valueField: '_id',
        skipSpecial: true,
      },
      placeholder: 'Rechtsträger auswählen',
      required: false,
      minWidth: '180px',
    },
    copagoBranch: {
      label: 'Copago Branch',
      api: { itemField: 'copagoBranch' },
      type: 'number',
      required: false,
      minWidth: '140px',
    },
    memberCardNumberPrefix: {
      label: 'Mitgliedskarten-Präfix',
      api: { itemField: 'memberCardNumberPrefix' },
      type: 'text',
      required: false,
      minWidth: '160px',
    },
    memberCardRequirement: {
      label: 'Mitgliedskarte erforderlich',
      api: { itemField: 'memberCardRequirement' },
      type: 'select',
      required: false,
      minWidth: '200px',
      options: [
        { value: 'inactive', label: 'Inaktiv' },
        { value: 'optional', label: 'Optional' },
        { value: 'required', label: 'Erforderlich' },
      ],
    },
    tags: {
      label: 'Tags (kommagetrennt)',
      api: { itemField: 'tags' },
      type: 'array',
      placeholder: 'z.B. kids, party',
      displayValue: value => (Array.isArray(value) ? value.join(', ') : '—'),
      transformValue: value =>
        value
          .toString()
          .split(',')
          .map(v => v.trim())
          .filter(Boolean),
      minWidth: '180px',
    },
    stateCodes: {
      label: 'Bundesländer',
      type: 'multi-select',
      placeholder: 'Bundesländer auswählen',
      required: false,
      minWidth: '180px',
      options: [
        { value: 'BW', label: 'Baden-Württemberg' },
        { value: 'BY', label: 'Bayern' },
        { value: 'BE', label: 'Berlin' },
        { value: 'BB', label: 'Brandenburg' },
        { value: 'HB', label: 'Bremen' },
        { value: 'HH', label: 'Hamburg' },
        { value: 'HE', label: 'Hessen' },
        { value: 'MV', label: 'Mecklenburg-Vorpommern' },
        { value: 'NI', label: 'Niedersachsen' },
        { value: 'NW', label: 'Nordrhein-Westfalen' },
        { value: 'RP', label: 'Rheinland-Pfalz' },
        { value: 'SL', label: 'Saarland' },
        { value: 'SN', label: 'Sachsen' },
        { value: 'ST', label: 'Sachsen-Anhalt' },
        { value: 'SH', label: 'Schleswig-Holstein' },
        { value: 'TH', label: 'Thüringen' },
      ],
    },
    comdeskSkillId: {
      label: 'Comdesk Skill ID',
      api: { itemField: 'comdeskSkillId' },
      placeholder: 'Skill-ID eingeben',
      type: 'text',
      required: false,
      minWidth: '120px',
    },
    printerStatus: {
      label: 'Druckerstatus',
      type: 'select',
      api: {
        fieldName: 'printerStatus',
        itemField: 'printerStatus',
        idName: '_id',
        resource: 'printer-status',
        labelField: 'name',
        valueField: '_id',
        skipSpecial: true,
      },
      placeholder: 'Drucker auswählen',
      required: false,
      minWidth: '200px',
    },
    assignedRegiondoProductIDs: {
      label: 'Regiondo Produkt-IDs (kommagetrennt)',
      api: { itemField: 'assignedRegiondoProductIDs' },
      type: 'array',
      placeholder: '123, 456, 789',
      displayValue: value => (Array.isArray(value) ? value.join(', ') : '—'),
      transformValue: value =>
        value
          .toString()
          .split(',')
          .map(v => v.trim())
          .filter(Boolean),
      minWidth: '240px',
    },
    ltCapacityMode: {
      label: 'Kapazitätsmodus',
      api: { itemField: 'ltCapacityMode' },
      type: 'select',
      required: false,
      minWidth: '180px',
      options: [
        { value: 'standard', label: 'Standard' },
        { value: 'ageSplit', label: 'Altersgetrennt' },
      ],
    },
    ltSlotCapacity: {
      label: 'LT Slot Kapazität',
      api: { itemField: 'ltSlotCapacity' },
      type: 'number',
      required: false,
      minWidth: '140px',
    },
    ltSlotRealCapacity: {
      label: 'LT Slot Real-Kapazität',
      api: { itemField: 'ltSlotRealCapacity' },
      type: 'number',
      required: false,
      minWidth: '160px',
    },
    ltSlottingMinimum: {
      label: 'LT Slot Minimum',
      api: { itemField: 'ltSlottingMinimum' },
      type: 'number',
      required: false,
      minWidth: '140px',
    },
    laneCount: {
      label: 'Bahnenanzahl',
      api: { itemField: 'laneCount' },
      type: 'number',
      required: false,
      minWidth: '140px',
    },
    maxTableNumber: {
      label: 'Max. Tischnummer',
      api: { itemField: 'maxTableNumber' },
      type: 'number',
      required: false,
      minWidth: '160px',
    },
    unusableTables: {
      label: 'Defekte Tische (kommagetrennt)',
      api: { itemField: 'unusableTables' },
      type: 'array',
      placeholder: 'z.B. 5, 6',
      displayValue: value =>
        Array.isArray(value) && value.length > 0 ? value.join(', ') : '—',
      transformValue: value =>
        value
          .toString()
          .split(',')
          .map(v => Number(v.trim()))
          .filter(v => Number.isInteger(v) && v > 0),
      minWidth: '200px',
    },
    pbSlotCapacity: {
      label: 'PB Slot Kapazität',
      api: { itemField: 'pbSlotCapacity' },
      type: 'number',
      required: false,
      minWidth: '140px',
    },
    participantLaneDensity: {
      label: 'Teilnehmer pro Bahn',
      api: { itemField: 'participantLaneDensity' },
      type: 'number',
      required: false,
      minWidth: '160px',
    },
    bookingWindowDistance: {
      label: 'Buchungsfenster (Minuten)',
      api: { itemField: 'bookingWindowDistance' },
      type: 'number',
      required: false,
      minWidth: '180px',
    },
    advanceBookingDeadlineHours: {
      label: 'Vorausbuchungsfrist (Stunden)',
      api: { itemField: 'advanceBookingDeadlineHours' },
      type: 'number',
      required: false,
      minWidth: '220px',
    },
    allowConsecutiveSlotBooking: {
      label: 'Folgeslot direkt buchbar',
      api: { itemField: 'allowConsecutiveSlotBooking' },
      type: 'boolean',
      required: false,
      minWidth: '190px',
    },
    alwaysRequestBooking: {
      label: 'Immer Buchungsanfrage',
      api: { itemField: 'alwaysRequestBooking' },
      type: 'boolean',
      required: false,
      minWidth: '170px',
    },
    offlinePaymentCardsOnly: {
      label: 'Vor-Ort-Zahlung nur mit Karte',
      api: { itemField: 'offlinePaymentCardsOnly' },
      type: 'boolean',
      required: false,
      minWidth: '190px',
    },
    offlinePaymentFee: {
      label: 'Vor-Ort-Zahlungsgebühr (Override, €)',
      api: { itemField: 'offlinePaymentFee' },
      type: 'number',
      required: false,
      min: 0,
      minWidth: '220px',
      placeholder: 'Leer = Produkt-Standard',
      description:
        'Überschreibt die Servicepauschale für Vor-Ort-Zahlung für alle Buchungen dieses Entertainments. Leer lassen = Standard aus der Produkt-Preisliste, 0 = Gebühr entfällt.',
      transformValue: value => {
        if (value === '' || value === null || value === undefined) return null;
        const n = Number(value);
        return Number.isFinite(n) && n >= 0 ? n : null;
      },
      displayValue: value =>
        value === null || value === undefined || value === ''
          ? '—'
          : `${Number(value).toFixed(2)} €`,
    },
    informManagerRoleIds: {
      label: 'Buchungsinfo an Rollen',
      type: 'multi-select',
      api: {
        fieldName: 'role',
        itemField: 'informManagerRoleIds',
        idName: '_id',
        resource: 'role',
        labelField: 'readableName',
        valueField: '_id',
        skipSpecial: true,
      },
      placeholder: 'Rollen auswählen',
      required: false,
      minWidth: '220px',
    },
    regiondoAutoSyncActive: {
      label: 'Regiondo Auto-Sync aktiv',
      api: { itemField: 'regiondoAutoSyncActive' },
      type: 'boolean',
      required: false,
      minWidth: '160px',
    },
    autoSlotCalcActive: {
      label: 'Auto Slot-Berechnung aktiv',
      api: { itemField: 'autoSlotCalcActive' },
      type: 'boolean',
      required: false,
      minWidth: '160px',
    },
    phone: {
      label: 'Telefon',
      api: { itemField: 'phone' },
      type: 'text',
      required: false,
      minWidth: '160px',
    },
    mail: {
      label: 'E-Mail',
      api: { itemField: 'mail' },
      type: 'text',
      required: false,
      minWidth: '200px',
    },
    getResponseCampaignId: {
      label: 'GetResponse Kampagnen-ID',
      api: { itemField: 'getResponseCampaignId' },
      type: 'text',
      required: false,
      minWidth: '200px',
      placeholder: 'optional, zentrale Pflege unter GetResponse Marketing',
    },
    homepage: {
      label: 'Homepage',
      api: { itemField: 'homepage' },
      type: 'text',
      required: false,
      minWidth: '220px',
    },
    mapsLink: {
      label: 'Google Maps Link',
      api: { itemField: 'mapsLink' },
      type: 'text',
      required: false,
      minWidth: '200px',
      truncate: 20,
      truncateMiddle: true,
    },
    reviewLink: {
      label: 'Bewertungslink',
      api: { itemField: 'reviewLink' },
      type: 'text',
      required: false,
      minWidth: '220px',
      truncate: 20,
      truncateMiddle: true,
    },
    googleLocationName: {
      label: 'Google-Standort',
      type: 'select',
      api: {
        fieldName: 'googleLocationName',
        itemField: 'googleLocationName',
        resource: 'googleReview/locations',
        labelField: 'title',
        valueField: 'resourceName',
        skipSpecial: true,
      },
      placeholder: 'Kein Google-Standort verknüpft',
      required: false,
      minWidth: '260px',
      description:
        'Welchen Standort aus Google Business Profile gehört zu diesem Entertainment? Wird für die Google-Rezensionen-Ansicht verwendet.',
    },
    experienceFeedbackAiPrompt: {
      label: 'KI-Prompt Kundenfeedback',
      api: { itemField: 'experienceFeedbackAiPrompt' },
      type: 'text',
      required: false,
      minWidth: '300px',
    },
    maxCourtesyVoucherAmount: {
      label: 'Kulanz-Gutschein Limit (€)',
      api: { itemField: 'maxCourtesyVoucherAmount' },
      type: 'number',
      required: false,
      minWidth: '160px',
      description: 'Max. Gutscheinwert ohne Sonderfreigabe (Standard: 50 €)',
    },
    showRegiondoBookings: {
      label: 'Regiondo-Buchungen anzeigen',
      api: { itemField: 'showRegiondoBookings' },
      type: 'boolean',
      required: false,
      minWidth: '160px',
    },
    showInternalBookings: {
      label: 'Interne Buchungen anzeigen',
      api: { itemField: 'showInternalBookings' },
      type: 'boolean',
      required: false,
      minWidth: '160px',
    },
    isExternallyManaged: {
      label: 'Extern verwaltet',
      api: { itemField: 'isExternallyManaged' },
      type: 'boolean',
      required: false,
      minWidth: '160px',
    },
    prepTimeMinutes: {
      label: 'Vorbereitungszeit (Minuten)',
      api: { itemField: 'prepTimeMinutes' },
      type: 'number',
      required: false,
      minWidth: '200px',
    },
    appearEarlyMinutes: {
      label: 'Früher erscheinen (Minuten)',
      api: { itemField: 'appearEarlyMinutes' },
      type: 'number',
      required: false,
      min: 0,
      placeholder: 'z.B. 15',
      transformValue: value => {
        if (value === '' || value === null || value === undefined) return null;
        const n = Number.parseInt(String(value), 10);
        return Number.isFinite(n) ? n : null;
      },
      minWidth: '220px',
    },
    mailHint: {
      label: 'Hinweis in Bestätigungsmail (rote Box)',
      api: { itemField: 'mailHint' },
      type: 'text',
      required: false,
      placeholder: 'z.B. Bauarbeiten im Eingangsbereich – bitte Seiteneingang nutzen',
      transformValue: value => {
        if (value === null || value === undefined) return null;
        const trimmed = String(value).trim();
        return trimmed === '' ? null : trimmed;
      },
      minWidth: '320px',
    },
    waiverText: {
      label: 'Haftungserklärung – Text (Waiver)',
      api: { itemField: 'waiverText' },
      type: 'text',
      required: false,
      multiline: true,
      rows: 6,
      excludeFromList: true,
      placeholder: 'Wortlaut der Haftungserklärung eingeben',
      description:
        'Wenn ausgefüllt, erhält der Kunde mit der Buchungsbestätigung einen Link zum Online-Waiver und eine Erinnerung eine Woche vor dem Termin.',
      transformValue: value => {
        if (value === null || value === undefined) return null;
        const trimmed = String(value).trim();
        return trimmed === '' ? null : trimmed;
      },
      minWidth: '320px',
    },
    waiverTitle: {
      label: 'Haftungserklärung – Titel (optional)',
      api: { itemField: 'waiverTitle' },
      type: 'text',
      required: false,
      excludeFromList: true,
      placeholder: 'z.B. Haftungsausschluss',
      transformValue: value => {
        if (value === null || value === undefined) return null;
        const trimmed = String(value).trim();
        return trimmed === '' ? null : trimmed;
      },
      minWidth: '240px',
    },
    bookingConfirmationLocationOverride: {
      label: 'Ort in Buchungsbestätigung',
      api: { itemField: 'bookingConfirmationLocationOverride' },
      type: 'object',
      required: false,
      excludeFromAdd: true,
      excludeFromList: true,
      displayValue: value => {
        if (!value || typeof value !== 'object') return 'Standard vom Rechtsträger';
        const parts = [value.legalName, value.street, value.postcode, value.city]
          .map(optionalLocationText)
          .filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : 'Standard vom Rechtsträger';
      },
      schema: {
        legalName: {
          label: 'Name/Firma',
          type: 'text',
          required: false,
          placeholder: 'Leer = Rechtsträger',
          transformValue: optionalLocationText,
        },
        street: {
          label: 'Straße',
          type: 'text',
          required: false,
          placeholder: 'Leer = Rechtsträger',
          transformValue: optionalLocationText,
        },
        postcode: {
          label: 'PLZ',
          type: 'text',
          required: false,
          placeholder: 'Leer = Rechtsträger',
          transformValue: optionalLocationText,
        },
        city: {
          label: 'Ort',
          type: 'text',
          required: false,
          placeholder: 'Leer = Rechtsträger',
          transformValue: optionalLocationText,
        },
      },
    },
    autoAnnouncementDefaults: {
      label: 'Auto-Ansagen (Standard)',
      type: 'object',
      schema: {
        autoGameStartSlotted: {
          label: 'Spielstart (Slots, Sekunden)',
          type: 'number',
          required: false,
        },
        autoGameStartAll: {
          label: 'Spielstart (alle, Sekunden)',
          type: 'number',
          required: false,
        },
        autoBriefingSlotted: {
          label: 'Briefing (Slots, Sekunden)',
          type: 'number',
          required: false,
        },
        autoBriefingAll: {
          label: 'Briefing (alle, Sekunden)',
          type: 'number',
          required: false,
        },
      },
    },
    prepTimeWarnings: {
      label: 'Vorbereitungszeit Warnungen',
      type: 'nested',
      addButtonText: 'Neue Warnung hinzufügen',
      excludeFromAdd: true,
      api: { itemField: 'prepTimeWarnings', nestedEndpoint: 'prepTimeWarning' },
      schema: {
        fromMinutes: { label: 'Ab Minuten', type: 'number', required: true },
        type: {
          label: 'Typ',
          type: 'select',
          required: true,
          options: [
            { value: 'info', label: 'Info' },
            { value: 'warning', label: 'Warnung' },
            { value: 'danger', label: 'Gefahr' },
          ],
        },
        message: { label: 'Nachricht', type: 'text', required: true },
      },
    },
    upgradeAdvanceBookingRules: {
      label: 'Buchungsvorlauf für Pakete/Upsells',
      type: 'nested',
      addButtonText: 'Neue Vorlaufregel hinzufügen',
      excludeFromAdd: true,
      api: {
        itemField: 'upgradeAdvanceBookingRules',
        nestedEndpoint: 'upgradeAdvanceBookingRule',
      },
      schema: {
        upgradeId: {
          label: 'Paket/Upsell',
          type: 'select',
          required: true,
          api: {
            fieldName: 'upgrade',
            itemField: 'upgradeId',
            idName: '_id',
            resource: 'upgrade',
            labelField: 'name',
            valueField: '_id',
            skipSpecial: true,
          },
          placeholder: 'Paket oder Upsell auswählen',
          minWidth: '260px',
        },
        minimumDaysBeforeBooking: {
          label: 'Mindesttage vor Buchung',
          type: 'number',
          required: true,
          min: 0,
          placeholder: 'z.B. 3',
          transformValue: value => {
            const parsed = Number(value);
            return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : 0;
          },
          minWidth: '220px',
        },
      },
    },
    smsTexts: {
      label: 'SMS Texte',
      type: 'nested',
      addButtonText: 'Neuen SMS-Text hinzufügen',
      excludeFromAdd: true,
      api: { itemField: 'smsTexts', nestedEndpoint: 'smsText' },
      schema: {
        type: { label: 'Typ', type: 'text', required: true },
        text: { label: 'Text', type: 'text', required: true },
        intervals: {
          label: 'Intervalle',
          type: 'nested',
          addButtonText: 'Intervall hinzufügen',
          excludeFromAdd: true,
          api: { itemField: 'intervals', nestedEndpoint: 'interval' },
          schema: {
            minHours: { label: 'Min. Stunden', type: 'number', required: true },
            sendAtHours: { label: 'Sendezeit (h)', type: 'number', required: true },
          },
        },
      },
    },
    mailTexts: {
      label: 'Mail Texte',
      type: 'nested',
      addButtonText: 'Neuen Mail-Text hinzufügen',
      excludeFromAdd: true,
      api: { itemField: 'mailTexts', nestedEndpoint: 'mailText' },
      schema: {
        type: { label: 'Typ', type: 'text', required: true },
        intervals: {
          label: 'Intervalle',
          type: 'nested',
          addButtonText: 'Intervall hinzufügen',
          excludeFromAdd: true,
          api: { itemField: 'intervals', nestedEndpoint: 'interval' },
          schema: {
            minHours: { label: 'Min. Stunden', type: 'number', required: true },
            sendAtHours: { label: 'Sendezeit (h)', type: 'number', required: true },
          },
        },
      },
    },
    resourceIds: {
      label: 'Ressourcen',
      type: 'multi-select',
      api: {
        fieldName: 'resource',
        itemField: 'resourceIds',
        idName: '_id',
        resource: 'resource',
        labelField: 'name',
        valueField: '_id',
        skipSpecial: true,
      },
      placeholder: 'Ressourcen auswählen',
      required: false,
      minWidth: '220px',
    },
    orderedResources: {
      label: 'Geordnete Ressourcen',
      type: 'nested',
      addButtonText: 'Geordnete Ressourcen konfigurieren',
      excludeFromAdd: true,
      singleObject: true,
      api: { itemField: 'orderedResources', nestedEndpoint: 'orderedResources' },
      schema: {
        startAtAnyPosition: {
          label: 'Start an beliebiger Position',
          type: 'boolean',
          required: false,
          minWidth: '200px',
        },
        maxBookingGroups: {
          label: 'Max. parallel nutzbare Ressourcen',
          type: 'number',
          required: false,
          minWidth: '260px',
        },
        intervalRules: {
          label: 'Intervallregeln',
          excludeFromAdd: true,
          type: 'object',
          schema: {
            startIntervalMinutes: {
              label: 'Startintervall (Minuten)',
              type: 'number',
              required: false,
              minWidth: '220px',
            },
            intervalPersonCapacity: {
              label: 'Kapazität pro Intervall (Personen)',
              type: 'number',
              required: false,
              minWidth: '280px',
            },
          },
          displayValue: value => {
            if (!value || typeof value !== 'object') return '—';
            const parts = [];
            if (Number.isFinite(value.startIntervalMinutes))
              parts.push(`${value.startIntervalMinutes} Min`);
            if (Number.isFinite(value.intervalPersonCapacity))
              parts.push(`Kapazität: ${value.intervalPersonCapacity}`);
            return parts.join(' | ') || '—';
          },
        },
        resources: {
          label: 'Reihenfolge & Dauern',
          type: 'nested',
          addButtonText: 'Ressource hinzufügen',
          excludeFromAdd: true,
          api: { itemField: 'resources', nestedEndpoint: 'resource' },
          schema: {
            resourceId: {
              label: 'Ressource',
              type: 'select',
              required: true,
              api: {
                fieldName: 'resource',
                itemField: 'resourceId',
                idName: '_id',
                resource: 'resource',
                labelField: 'name',
                valueField: '_id',
                skipSpecial: true,
              },
              placeholder: 'Ressource auswählen',
              minWidth: '220px',
            },
            orderPosition: {
              label: 'Position',
              type: 'number',
              required: true,
              minWidth: '120px',
            },
            trackId: {
              label: 'Strang/Track',
              type: 'text',
              required: false,
              placeholder: 'z.B. A oder B',
              minWidth: '140px',
            },
            resourceDurationMinutes: {
              label: 'Dauer (Minuten)',
              type: 'number',
              required: false,
              minWidth: '160px',
            },
          },
        },
      },
    },
    assignedComdeskAgents: {
      label: 'Comdesk Agenten',
      type: 'nested',
      addButtonText: 'Neuen Comdesk Agenten hinzufügen',
      excludeFromAdd: true,
      api: {
        itemField: 'assignedComdeskAgents',
        nestedEndpoint: 'agent',
      },
      schema: {
        id: {
          label: 'Agent ID',
          type: 'text',
          required: true,
        },
        description: {
          label: 'Beschreibung',
          type: 'text',
          required: true,
        },
        skillGroups: {
          label: 'Skill Groups',
          type: 'array',
          placeholder: 'Skill Groups (kommagetrennt)',
          displayValue: value => (Array.isArray(value) ? value.join(', ') : '—'),
          transformValue: value =>
            value
              .toString()
              .split(',')
              .map(v => v.trim()),
          listDisplayValue: value => {
            if (!value || !Array.isArray(value)) return '—';
            if (value.length === 0) return 'Keine';
            if (value.length <= 3) return value.join(', ');
            return `${value.slice(0, 3).join(', ')} (+${value.length - 3} weitere)`;
          },
        },
      },
    },
  };

  return (
    <Box sx={viewRootTextPrimarySx}>
    {authCtx.checkRight('googleReview:read:own') && (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button
          variant='outlined'
          startIcon={<StarIcon sx={{ color: '#FBBC04' }} />}
          onClick={() => setGoogleRatingOpen(true)}
        >
          Google-Bewertungen
        </Button>
      </Box>
    )}
    <EditableList
      schema={entertainmentSchema}
      title='Neues Entertainment anlegen'
      resourceType='entertainment'
      headerSearchPlaceholder='Entertainments durchsuchen'
      parentIsEditable={authCtx.checkRight('entertainment:update')}
      options={{
        titleKey: 'longName',
      }}
      visibleColumnKeys={[
        'longName',
        'branch',
        'tags',
        'stateCodes',
        'phone',
        'mail',
        'homepage',
        'mapsLink',
        'reviewLink',
        'googleLocationName',
        'experienceFeedbackAiPrompt',
        'maxCourtesyVoucherAmount',
        'advanceBookingDeadlineHours',
        'allowConsecutiveSlotBooking',
        'alwaysRequestBooking',
        'offlinePaymentFee',
        'isExternallyManaged',
        'informManagerRoleIds',
        'appearEarlyMinutes',
        'mailHint',
        'upgradeAdvanceBookingRules',
        'resourceIds',
        'orderedResources',
      ]}
    />
    <GoogleRatingMatchDialog
      open={googleRatingOpen}
      onClose={() => setGoogleRatingOpen(false)}
    />
    </Box>
  );
}

export default EntertainmentManagement;
